/**
 * Updates J-code / CPT table data for an order via the backend.
 * @module updateCPTtables
 */

import { JCodeDetails } from "../../data-model/medicalPaOrdersModel";
import { API_ENDPOINTS } from "../../enums/apiUrls";
import { throwApiError } from "../throwApiError";
import { getBearerToken } from "./bearerToken";

/** Request body for the update-J-codes (CPT tables) endpoint. */
export interface UpdateCPTtablesPayload {
  order_id: string;
  org_id: string;
  j_codes: JCodeDetails[];
}

/**
 * POSTs a payload that updates J-codes (CPT tables) for the current order.
 *
 * Failures follow the `/prior-auth-utilities/*` HTTP-status contract: 4xx/5xx
 * responses throw an `Error` enriched with `status` and `detail` (read by
 * `utils/showApiError` on the consumer side).
 *
 * @param payload - Order, org, and J-code rows to persist.
 * @param signal - Optional AbortSignal to cancel the in-flight request.
 * @returns Parsed JSON response from the server.
 * @throws {Error & { status: number; detail?: string }} On HTTP failure.
 * @throws {Error} On authentication, network, or JSON parse failure.
 */
export const updateCPTtables = async (
  payload: UpdateCPTtablesPayload,
  signal?: AbortSignal,
) => {
  try {
    const token = await getBearerToken();
    const response = await fetch(API_ENDPOINTS.UPDATE_J_CODES, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
      ...(signal && { signal }),
    });

    if (!response.ok) {
      await throwApiError(response);
    }
    return await response.json();
  } catch (error) {
    console.error("Failed to update CPT tables:", error);
    throw error;
  }
};
